import { useEffect, useState } from "react";
import { Box, Typography, Dialog, DialogContent, IconButton, Button, Chip, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { dataGridStyle, colors } from "../../../../theme";
import { TOEstablishTheColorOfLog, handleCellIPClick, renderTypeCell } from "../../../../components/ImportantFunctions";
import API from "../../../../api";

const PROTOCOLS = [
  { key: "DNS", label: "DNS", color: "#D0C366" },
  { key: "RDP", label: "RDP", color: "#FEA51E" },
  { key: "SMB", label: "SMB", color: "#af696a" },
  { key: "WEB", label: "WEB", color: "#58A8AF" },
];

const columns = [
  {
    field: "timeStamp",
    headerName: "TIMESTAMP",
    minWidth: 180,
    filterable: true,
    type: "date",
    valueFormatter: (value) => {
      if (value == null) return "";
      try { return new Date(value).toLocaleString(); } catch { return ""; }
    },
  },
  { field: "ipAttaccante", headerName: "SOURCE", minWidth: 130 },
  { field: "ipDestinatario", headerName: "DESTINATION", minWidth: 130 },
  { field: "tipo", headerName: "TYPE", width: 150, renderCell: renderTypeCell },
  {
    field: "dettaglio",
    headerName: "DETAILS",
    flex: 1,
    minWidth: 250,
    filterable: true,
    renderCell: (params) => <span style={{ color: TOEstablishTheColorOfLog(params.value) }}>{params.value}</span>,
  },
];

// Protocol breakdown for this asset's security logs, classified by keyword
// (DNS/RDP/SMB/WEB) rather than by conduit service field — ported from the
// removed "Search by IP" page's ProtocolAnalyzer.jsx. Each card opens a
// filterable log grid; clicking a source/destination IP cell there jumps to
// Inventory filtered on that IP (handleCellIPClick).
const ProtocolAnalyzer = ({ ip, multiSecurityView = false }) => {
  const [separedData, setSeparedData] = useState({ DNS: [], SMB: [], RDP: [], WEB: [] });
  const [openDialog, setOpenDialog] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableTitle, setTableTitle] = useState("");

  useEffect(() => {
    if (!ip) return;
    const apiCall = multiSecurityView ? API.getProtocolAnalyzerAllLecs : API.getProtocolAnalyzer;
    apiCall({ ip }).then((res) => {
      const transformed = (res.data || []).reduce((acc, item) => {
        acc[item.keyWord] = acc[item.keyWord] ? [...acc[item.keyWord], ...item.data] : item.data;
        return acc;
      }, {});
      setSeparedData({ DNS: [], SMB: [], RDP: [], WEB: [], ...transformed });
    }).catch((err) => console.error("[ProtocolAnalyzer] Error fetching protocol analyzer:", err));
  }, [ip, multiSecurityView]);

  const openProtocolTable = (key) => {
    const rows = (separedData[key] || []).map((item) => ({ id: item._id, ...item }));
    setTableData(rows);
    setTableTitle(key);
    setOpenDialog(true);
  };

  return (
    <Box sx={{ borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, p: 1.5, height: "100%", overflowY: "auto" }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
        Protocol Analyzer
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
        {PROTOCOLS.map(({ key, label, color }) => {
          const entries = separedData[key] || [];
          const latest = entries[0];
          return (
            <Box
              key={key}
              sx={{ p: 1, borderRadius: 1, backgroundColor: "#222", border: `1px solid ${colors.grey[800]}` }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.grey[100], minWidth: 56 }}>
                  {label}
                </Typography>
                <Chip
                  label={entries.length}
                  size="small"
                  sx={{ bgcolor: `${color}22`, color, fontWeight: 700, fontSize: 11, height: 20 }}
                />
                <Typography sx={{ fontSize: 11.5, color: colors.grey[400], ml: "auto", whiteSpace: "nowrap" }}>
                  {latest ? `Last seen: ${latest.timeStamp}` : ""}
                </Typography>
                <Button
                  size="small"
                  disabled={entries.length === 0}
                  onClick={() => openProtocolTable(key)}
                  sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "none", color: "#459095", flexShrink: 0 }}
                >
                  View More
                </Button>
              </Box>
              {latest ? (
                <Tooltip title={latest.dettaglio} arrow placement="bottom-start">
                  <Typography noWrap sx={{ fontSize: 11, fontWeight: 600, color: TOEstablishTheColorOfLog(latest.dettaglio), cursor: "default", mt: 0.5, pl: 2.2 }}>
                    {latest.dettaglio}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: 10.5, color: colors.grey[500], mt: 0.5, pl: 2.2 }}>No activity in range</Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} sx={{ "& .MuiDialog-paper": { width: "100%", height: "100%", maxWidth: "none", backgroundColor: "#1c1c1c", backgroundImage: "none" } }}>
        <IconButton onClick={() => setOpenDialog(false)} sx={{ position: "absolute", right: 8, top: 8, color: (t) => t.palette.grey[400] }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.grey[100], mb: 1.5 }}>{tableTitle} logs for {ip}</Typography>
          <Box sx={{ height: "85%" }}>
            <DataGridPro
              sx={dataGridStyle}
              rows={tableData}
              columns={columns}
              pagination
              autoPageSize
              onCellClick={(params) => handleCellIPClick(params, "security")}
              slots={{ toolbar: GridToolbar }}
              slotProps={{ toolbar: { printOptions: { disableToolbarButton: true } } }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProtocolAnalyzer;
